import countdownSound from '../audio/countdown.wav';
import { unwrapApiResponse } from './promoNovosLojistas';

/**
 * Banner de "Subidas de Lotes".
 * Os horários (HH:MM) da config são sempre interpretados no fuso America/Sao_Paulo,
 * independente do fuso do navegador. Aqui derivamos o "agora em SP" com Intl (sem
 * dependência de biblioteca de timezone).
 */

export const SAO_PAULO_TZ = 'America/Sao_Paulo';

// Estados do banner
export const STATE_UPCOMING = 1; // próxima janela (mensagem estática)
export const STATE_COUNTDOWN = 2; // contagem regressiva (última 1h)
export const STATE_LAUNCHED = 3; // lançamento (15 min)

export const COUNTDOWN_THRESHOLD_MS = 60 * 60 * 1000; // 1h → entra na contagem
export const LAUNCH_DURATION_MS = 15 * 60 * 1000; // 15min de estado de lançamento
export const PULSE_THRESHOLD_SECONDS = 10 * 60; // a partir de 10min o timer pulsa
export const SOUND_LEAD_SECONDS = 4; // som começa 4s antes de zerar

// Horizonte de dias à frente para achar a próxima subida (cobre semanas com poucos
// dias de operação, pulando dias sem operação).
export const HORIZON_DAYS = 8;

// Som do alerta (4s antes de zerar). O import é resolvido pelo webpack (file-loader)
// para a URL do asset empacotado.
export const SOUND_URL = countdownSound;

/** Faz o parse de "HH:MM" (24h). Retorna { hours, minutes } ou null. */
export const parseHHMM = value => {
  if (typeof value !== 'string') {
    return null;
  }
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!match) {
    return null;
  }
  return { hours: Number(match[1]), minutes: Number(match[2]) };
};

/** "13:00" → "13h00" (formato exibido nas mensagens). */
export const formatReferenceLabel = value => {
  const hhmm = parseHHMM(value);
  if (!hhmm) {
    return '';
  }
  return `${String(hhmm.hours).padStart(2, '0')}h${String(hhmm.minutes).padStart(2, '0')}`;
};

/** Normaliza uma lista de janelas (dias) vinda da API/Pusher. */
export const parseLaunchWindowsList = list =>
  (Array.isArray(list) ? list : [])
    .map(day => ({
      dayOfWeek: Number(day?.dayOfWeek),
      hasOperation: Boolean(day?.hasOperation),
      lots: Array.isArray(day?.lots)
        ? day.lots.map(lot => ({
            name: typeof lot?.name === 'string' ? lot.name : '',
            earlyAccessTime: lot?.earlyAccessTime || '',
            generalReleaseTime: lot?.generalReleaseTime || '',
          }))
        : [],
    }))
    .filter(day => Number.isInteger(day.dayOfWeek) && day.dayOfWeek >= 0 && day.dayOfWeek <= 6);

/** Desembrulha o GET /auction-config/launch-windows → array de dias. */
export const parseLaunchWindowsResponse = response => {
  const raw = unwrapApiResponse(response) ?? {};
  const list = Array.isArray(raw.launchWindows) ? raw.launchWindows : Array.isArray(raw) ? raw : [];
  return parseLaunchWindowsList(list);
};

/** Componentes do "agora" (ou de qualquer epoch) no fuso America/Sao_Paulo. */
export const getSaoPauloParts = epoch => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: SAO_PAULO_TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = formatter.formatToParts(new Date(epoch)).reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  let hour = Number(parts.hour);
  if (hour === 24) {
    hour = 0; // alguns ambientes emitem '24' à meia-noite
  }

  return {
    year: Number(parts.year),
    month: Number(parts.month), // 1-12
    day: Number(parts.day),
    hour,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
};

/**
 * Offset (ms) do fuso SP em relação ao UTC, derivado de `epoch`.
 * Brasil não tem horário de verão hoje (offset constante -03:00); calcular a partir
 * do "agora" é suficiente e evita depender de tabela de timezone.
 */
export const getSaoPauloOffsetMs = epoch => {
  const p = getSaoPauloParts(epoch);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const base = Math.floor(epoch / 1000) * 1000;
  return Math.round((asUtc - base) / 60000) * 60000;
};

/** Epoch (ms) de um horário HH:MM em SP para uma data-calendário. */
const saoPauloWallToEpoch = (year, month0, day, hours, minutes, offsetMs) =>
  Date.UTC(year, month0, day, hours, minutes, 0) - offsetMs;

/** Horário de referência daquela loja para um lote (clube → antecipado; senão geral). */
export const getReferenceTime = (lot, benefitsClub) =>
  benefitsClub ? lot.earlyAccessTime : lot.generalReleaseTime;

const timeToMinutes = hhmm => hhmm.hours * 60 + hhmm.minutes;

/**
 * Lote a que o veículo pertence dentro de um dia: o de earlyAccessTime mais recente
 * que seja <= horário da subida do veículo; se nenhum, o de earlyAccessTime mais cedo.
 */
const pickLotForStart = (lots, startMinutes) => {
  let chosenAtOrBefore = null;
  let earliest = null;

  lots.forEach(lot => {
    const early = parseHHMM(lot.earlyAccessTime);
    if (!early) {
      return;
    }
    const minutes = timeToMinutes(early);

    if (!earliest || minutes < timeToMinutes(parseHHMM(earliest.earlyAccessTime))) {
      earliest = lot;
    }
    if (
      minutes <= startMinutes &&
      (!chosenAtOrBefore || minutes > timeToMinutes(parseHHMM(chosenAtOrBefore.earlyAccessTime)))
    ) {
      chosenAtOrBefore = lot;
    }
  });

  return chosenAtOrBefore || earliest;
};

/**
 * Liberação para quem NÃO é clube, baseada no lote do dia da subida do veículo
 * (weekday de `startTime` no fuso SP), e não mais em um tempo fixo após o startTime.
 *
 * Retorna:
 * - { status: 'gated', epoch } → libera quando now >= generalReleaseTime do lote naquele dia;
 * - { status: 'always' }       → dia da subida sem operação / sem lote (ex.: slot desativado
 *                                 depois que o veículo subiu) → continua mostrando;
 * - { status: 'unknown' }      → sem config carregada / startTime inválido → o chamador
 *                                 aplica um fallback (ex.: tempo fixo).
 */
export const resolveNonClubRelease = ({ days, startTime, now = Date.now() }) => {
  const startEpoch = typeof startTime === 'number' ? startTime : new Date(startTime).getTime();
  if (!Number.isFinite(startEpoch)) {
    return { status: 'unknown' };
  }

  const list = Array.isArray(days) ? days : [];
  if (list.length === 0) {
    return { status: 'unknown' };
  }

  const startParts = getSaoPauloParts(startEpoch);
  const subidaDayOfWeek = new Date(
    Date.UTC(startParts.year, startParts.month - 1, startParts.day)
  ).getUTCDay();

  const day = list.find(item => item.dayOfWeek === subidaDayOfWeek);
  if (!day || !day.hasOperation || !Array.isArray(day.lots) || day.lots.length === 0) {
    return { status: 'always' };
  }

  const lot = pickLotForStart(day.lots, startParts.hour * 60 + startParts.minute);
  const general = lot && parseHHMM(lot.generalReleaseTime);
  if (!general) {
    return { status: 'always' };
  }

  const offsetMs = getSaoPauloOffsetMs(now);
  const epoch = saoPauloWallToEpoch(
    startParts.year,
    startParts.month - 1,
    startParts.day,
    general.hours,
    general.minutes,
    offsetMs
  );

  return { status: 'gated', epoch };
};

/**
 * Estado do banner para a loja, dado o conjunto de janelas e o "agora".
 * Retorna { active, state, lotName, referenceStr, referenceEpoch, remainingSeconds, launchEndsAt }.
 */
export const computeBannerState = ({
  days,
  benefitsClub,
  now = Date.now(),
  horizonDays = HORIZON_DAYS,
}) => {
  const list = Array.isArray(days) ? days : [];
  if (list.length === 0) {
    return { active: false };
  }

  const byDayOfWeek = {};
  list.forEach(day => {
    byDayOfWeek[day.dayOfWeek] = day;
  });

  const offsetMs = getSaoPauloOffsetMs(now);
  const today = getSaoPauloParts(now);
  const baseCalendar = Date.UTC(today.year, today.month - 1, today.day);

  const events = [];
  for (let k = 0; k <= horizonDays; k += 1) {
    const dayDate = new Date(baseCalendar);
    dayDate.setUTCDate(dayDate.getUTCDate() + k);
    const cfg = byDayOfWeek[dayDate.getUTCDay()];

    if (cfg && cfg.hasOperation) {
      cfg.lots.forEach(lot => {
        const refStr = getReferenceTime(lot, benefitsClub);
        const hhmm = parseHHMM(refStr);
        if (!hhmm) {
          return;
        }
        events.push({
          name: lot.name,
          refStr,
          refEpoch: saoPauloWallToEpoch(
            dayDate.getUTCFullYear(),
            dayDate.getUTCMonth(),
            dayDate.getUTCDate(),
            hhmm.hours,
            hhmm.minutes,
            offsetMs
          ),
        });
      });
    }
  }

  if (events.length === 0) {
    return { active: false };
  }

  events.sort((a, b) => a.refEpoch - b.refEpoch);

  // Estado 3: dentro dos 15min após o horário de referência (lançamento).
  const launched = events.filter(
    event => now >= event.refEpoch && now < event.refEpoch + LAUNCH_DURATION_MS
  );
  if (launched.length > 0) {
    const event = launched[launched.length - 1];
    return {
      active: true,
      state: STATE_LAUNCHED,
      lotName: event.name,
      referenceStr: event.refStr,
      referenceEpoch: event.refEpoch,
      launchEndsAt: event.refEpoch + LAUNCH_DURATION_MS,
      remainingSeconds: 0,
    };
  }

  // Próxima subida à frente.
  const next = events.find(event => event.refEpoch > now);
  if (!next) {
    return { active: false };
  }

  const remainingMs = next.refEpoch - now;
  const remainingSeconds = Math.max(0, Math.round(remainingMs / 1000));
  const state = remainingMs <= COUNTDOWN_THRESHOLD_MS ? STATE_COUNTDOWN : STATE_UPCOMING;

  return {
    active: true,
    state,
    lotName: next.name,
    referenceStr: next.refStr,
    referenceEpoch: next.refEpoch,
    remainingSeconds,
  };
};

/** Toca o alerta sonoro uma única vez (no-op se SOUND_URL vazio ou autoplay bloqueado). */
export const playLaunchSound = audioRef => {
  if (!SOUND_URL) {
    return;
  }
  try {
    if (!audioRef.current) {
      audioRef.current = new Audio(SOUND_URL);
    }
    audioRef.current.currentTime = 0;
    const played = audioRef.current.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {});
    }
  } catch (error) {
    // Autoplay bloqueado / ambiente sem Audio — ignora silenciosamente.
  }
};
